import { useEffect } from "react";

/**
 * Activa el modo de dibujo a mano alzada sobre el mapa.
 * - onPolygonComplete se dispara con cada polígono dibujado.
 * - Si `stayActive` es true, el modo no se apaga al terminar un polígono
 *   (permite dibujar varios). Si es false o undefined, se apaga después
 *   del primero (comportamiento legacy).
 */
export const useDrawingMode = (
  isLoaded,
  map,
  isDrawingMode,
  onPolygonComplete,
  setIsDrawingMode,
  { stayActive = false } = {}
) => {
  useEffect(() => {
    if (!isLoaded || !map || !isDrawingMode) return;

    let drawing = false;
    let path = [];
    let tempPolyline = null;

    const handleMouseDown = (e) => {
      drawing = true;
      path = [e.latLng];
      tempPolyline = new window.google.maps.Polyline({
        path: path,
        strokeColor: "#FF0000",
        strokeOpacity: 1.0,
        strokeWeight: 2,
      });
      tempPolyline.setMap(map);
    };

    const handleMouseMove = (e) => {
      if (!drawing) return;
      path.push(e.latLng);
      if (tempPolyline) {
        tempPolyline.setPath(path);
      }
    };

    const handleMouseUp = () => {
      if (!drawing) return;
      drawing = false;
      if (tempPolyline) {
        tempPolyline.setMap(null);
      }
      if (path.length > 2) {
        path.push(path[0]);
        const newPolygon = new window.google.maps.Polygon({
          paths: path,
          strokeColor: "#FF0000",
          strokeOpacity: 0.7,
          strokeWeight: 2,
          fillColor: "#FF0000",
          fillOpacity: 0.12,
        });
        newPolygon.setMap(map);
        onPolygonComplete?.(newPolygon);
      }
      path = [];
      tempPolyline = null;
      if (!stayActive) setIsDrawingMode(false);
    };

    const mousedownListener = map.addListener("mousedown", handleMouseDown);
    const mousemoveListener = map.addListener("mousemove", handleMouseMove);
    const mouseupListener = map.addListener("mouseup", handleMouseUp);
    window.addEventListener("mouseup", handleMouseUp);

    map.setOptions({
      draggable: false,
      draggableCursor:
        "url('https://img.icons8.com/ios-filled/16/000000/pencil.png'), auto",
      scrollwheel: true,
    });

    return () => {
      window.google.maps.event.removeListener(mousedownListener);
      window.google.maps.event.removeListener(mousemoveListener);
      window.google.maps.event.removeListener(mouseupListener);
      window.removeEventListener("mouseup", handleMouseUp);
      // Si quedó una polyline en curso (user canceló/salió a mitad de dibujo),
      // la sacamos del mapa para no dejar rastro.
      if (tempPolyline) {
        tempPolyline.setMap(null);
        tempPolyline = null;
      }
      map.setOptions({
        draggable: true,
        draggableCursor: null,
        scrollwheel: true,
      });
    };
  }, [isLoaded, map, isDrawingMode, onPolygonComplete, setIsDrawingMode, stayActive]);
};

/**
 * Helpers para convertir entre una instancia google.maps.Polygon y un array
 * plano de {lat,lng}. Los usamos para persistir polígonos en la sesión.
 */
export const polygonToPath = (polygon) => {
  if (!polygon?.getPath) return [];
  return polygon.getPath().getArray().map((latLng) => ({
    lat: latLng.lat(),
    lng: latLng.lng(),
  }));
};

export const pathToPolygon = (path, map, options = {}) => {
  if (!path?.length || !window.google?.maps) return null;
  const poly = new window.google.maps.Polygon({
    paths: path.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) })),
    strokeColor: "#FF0000",
    strokeOpacity: 0.7,
    strokeWeight: 2,
    fillColor: "#FF0000",
    fillOpacity: 0.12,
    ...options,
  });
  if (map) poly.setMap(map);
  return poly;
};